// Slice 40 — typed fetch wrapper that talks to the license-server. Mirrors
// the spec §1 envelope so call-sites can pattern-match `{ data }` vs.
// `{ error: { code, message } }` without redoing the parse.
//
// Cookies are session-bound (HttpOnly), so `credentials: "include"` is the
// load-bearing line. Without it the browser sends nothing and the staff
// gate 401s every request.

export interface ApiError {
  code: string;
  message: string;
  status: number;
}

export interface ApiOk<T> {
  ok: true;
  data: T;
}

export interface ApiFail {
  ok: false;
  error: ApiError;
}

export type ApiResult<T> = ApiOk<T> | ApiFail;

export async function api<T = unknown>(
  method: "GET" | "POST" | "PATCH" | "PUT" | "DELETE",
  path: string,
  body?: unknown,
): Promise<ApiResult<T>> {
  let res: Response;
  try {
    const init: RequestInit = { method, credentials: "include" };
    if (body !== undefined) {
      init.headers = { "content-type": "application/json" };
      init.body = JSON.stringify(body);
    }
    res = await fetch(path, init);
  } catch (e) {
    return {
      ok: false,
      error: {
        code: "network",
        message: e instanceof Error ? e.message : "Network error",
        status: 0,
      },
    };
  }
  const contentType = res.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) {
    return {
      ok: false,
      error: {
        code: "bad_response",
        message: `Non-JSON response (${res.status}).`,
        status: res.status,
      },
    };
  }
  const parsed = (await res.json()) as { data?: T; error?: { code: string; message: string } };
  if (!res.ok || parsed.error) {
    const err = parsed.error ?? { code: "server_error", message: `HTTP ${res.status}` };
    return { ok: false, error: { ...err, status: res.status } };
  }
  return { ok: true, data: parsed.data as T };
}

// ─── typed convenience wrappers ──────────────────────────────────────────

export type StaffRole = "admin" | "staff";

export type SessionState =
  | { state: "needs_bootstrap" }
  | { state: "anonymous" }
  | { state: "authenticated"; username: string; role: StaffRole };

// ─── licenses (Slice 41) ─────────────────────────────────────────────────

export type Tier = "free" | "pro" | "beta";

export const FEATURE_FLAGS = [
  "multi-instance",
  "scheduled-backups",
  "modpack-installer",
  "s3-backup",
  "steamcmd-installers",
  "per-instance-roles",
] as const;
export type FeatureFlag = (typeof FEATURE_FLAGS)[number];

export interface LicenseRow {
  licenseId: string;
  customerId: string;
  tier: Tier;
  majorVersion: number;
  issuedAt: number;
  hwidFingerprint: string;
  /** JSON string of FeatureFlag[]; the DB stores it serialised. */
  features: string;
  maxInstances: number;
  expiresAt: number | null;
  betaProgram: boolean;
  revoked: boolean;
  revokedAt: number | null;
  revokedReason: string | null;
  createdAt: number;
}

export interface LicenseDetailRow extends LicenseRow {
  /** The full signed JSON the customer installs (omitted in list-rows). */
  signedJson: string;
}

export interface SignedLicense {
  version: 2;
  licenseId: string;
  customerId: string;
  tier: Tier;
  majorVersion: number;
  issuedAt: number;
  hwidFingerprint: string;
  features: FeatureFlag[];
  maxInstances: number;
  expiresAt?: number | null;
  betaProgram: boolean;
  signature: string;
}

export interface Page {
  cursor: string | null;
  limit: number;
}

export interface ListLicensesParams {
  cursor?: string | null | undefined;
  limit?: number | undefined;
  q?: string | undefined;
  revoked?: boolean | undefined;
  // Slice 55 — server-side tier + expiry filters.
  tier?: Tier | undefined;
  /** "expiring" (active + within 14d) | "expired" (active + past) */
  expiry?: "expiring" | "expired" | undefined;
}

export interface IssueLicenseInput {
  customerId: string;
  tier: Tier;
  maxInstances: number;
  majorVersion?: number;
  betaProgram?: boolean;
  hwidFingerprint?: string;
  features?: FeatureFlag[];
  /** unix seconds (perpetual license = omit). */
  expiresAt?: number;
}

function qs(params: Record<string, string | number | boolean | null | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export const Api = {
  session: () => api<SessionState>("GET", "/admin/session"),
  bootstrap: (username: string, password: string) =>
    api<{ username: string; role: StaffRole }>("POST", "/admin/bootstrap", { username, password }),
  login: (username: string, password: string) =>
    api<{ username: string; role: StaffRole }>("POST", "/admin/login", { username, password }),
  logout: () => api<{ ok: true }>("POST", "/admin/logout"),
  // Slice 49 (V2.2) — passkey login.
  beginPasskeyLogin: (username: string) =>
    api<unknown>("POST", "/admin/passkey/begin-login", { username }),
  finishPasskeyLogin: (response: unknown) =>
    api<{ username: string; role: StaffRole }>("POST", "/admin/passkey/finish-login", { response }),

  // Licenses (Slice 41) — list omits signedJson, detail includes it.
  listLicenses: (p: ListLicensesParams = {}) =>
    api<LicenseRow[]>(
      "GET",
      `/admin/licenses${qs({
        cursor: p.cursor ?? undefined,
        limit: p.limit ?? undefined,
        q: p.q ?? undefined,
        revoked: p.revoked === undefined ? undefined : p.revoked ? "true" : "false",
      })}`,
    ),
  getLicense: (id: string) =>
    api<LicenseDetailRow>("GET", `/admin/licenses/${encodeURIComponent(id)}`),
  issueLicense: (input: IssueLicenseInput) =>
    api<{ license: SignedLicense }>("POST", "/admin/licenses", input),
  revokeLicense: (id: string, reason: string) =>
    api<{ revoked: true }>("POST", `/admin/licenses/${encodeURIComponent(id)}/revoke`, { reason }),
  unrevokeLicense: (id: string) =>
    api<{ revoked: false }>("POST", `/admin/licenses/${encodeURIComponent(id)}/unrevoke`),
  // ── Slice 54 — license drill-down ──
  licenseTelemetry: (id: string, windowDays = 30) =>
    api<LicenseTelemetryRollup>(
      "GET",
      `/admin/licenses/${encodeURIComponent(id)}/telemetry?windowDays=${windowDays}`,
    ),
  extendLicense: (id: string, expiresAt: number | null) =>
    api<{ license: SignedLicense }>("POST", `/admin/licenses/${encodeURIComponent(id)}/extend`, {
      expiresAt,
    }),
  changeLicenseTier: (id: string, tier: Tier) =>
    api<{ license: SignedLicense }>(
      "POST",
      `/admin/licenses/${encodeURIComponent(id)}/change-tier`,
      { tier },
    ),
  resendSingleLicenseEmail: (id: string) =>
    api<{ sent: number }>("POST", `/admin/licenses/${encodeURIComponent(id)}/resend-email`),
  /** Anchor href for the download flow — never goes through `api()` because
   *  the response body is the raw signed JSON, not the {data} envelope. */
  licenseDownloadUrl: (id: string) => `/admin/licenses/${encodeURIComponent(id)}/download`,
} as const;

export interface LicenseTelemetryRollup {
  window: { fromTs: number; toTs: number; days: number };
  latestInstanceCount: number;
  latestSeenAt: number | null;
  commandInvocations: number;
  errorTotal: number;
  errorRate: number;
  series: Array<{
    bucketStart: number;
    instanceCount: number;
    commandInvocations: number;
    errorTotal: number;
  }>;
  topFeatures: Array<{ key: string; count: number }>;
  topErrors: Array<{ key: string; count: number }>;
  byVersion: Array<{ vyvernVersion: string; count: number }>;
}

/**
 * Listings come back as `{ data: LicenseRow[] }` per the spec §1 envelope;
 * the `page` field for cursor pagination is the *sibling* of `data`. The
 * generic `api()` only forwards `data`, so use this wrapper when the call-
 * site also needs the next-cursor.
 */
export async function listLicensesWithPage(
  p: ListLicensesParams = {},
): Promise<ApiResult<{ data: LicenseRow[]; page: Page }>> {
  return apiPaged<LicenseRow>(
    `/admin/licenses${qs({
      cursor: p.cursor ?? undefined,
      limit: p.limit ?? undefined,
      q: p.q ?? undefined,
      revoked: p.revoked === undefined ? undefined : p.revoked ? "true" : "false",
      tier: p.tier ?? undefined,
      expiry: p.expiry ?? undefined,
    })}`,
  );
}

/** Generic version of `listLicensesWithPage`: any endpoint that returns
 *  `{ data: T[], page }` can call through here. */
export async function apiPaged<T>(path: string): Promise<ApiResult<{ data: T[]; page: Page }>> {
  try {
    const res = await fetch(path, { credentials: "include" });
    const parsed = (await res.json()) as {
      data?: T[];
      page?: Page;
      error?: { code: string; message: string };
    };
    if (!res.ok || parsed.error) {
      const err = parsed.error ?? { code: "server_error", message: `HTTP ${res.status}` };
      return { ok: false, error: { ...err, status: res.status } };
    }
    return {
      ok: true,
      data: { data: parsed.data ?? [], page: parsed.page ?? { cursor: null, limit: 50 } },
    };
  } catch (e) {
    return {
      ok: false,
      error: {
        code: "network",
        message: e instanceof Error ? e.message : "Network error",
        status: 0,
      },
    };
  }
}

// ─── feedback (Slice 42) ─────────────────────────────────────────────────

export const FEEDBACK_STATUSES = ["new", "triaged", "in_progress", "closed"] as const;
export type FeedbackStatus = (typeof FEEDBACK_STATUSES)[number];

export const REDACTABLE_FEEDBACK_FIELDS = ["body", "screenshotBase64", "systemInfo"] as const;
export type RedactableFeedbackField = (typeof REDACTABLE_FEEDBACK_FIELDS)[number];

export interface FeedbackRow {
  id: string;
  licenseId: string | null;
  vyvernVersion: string;
  body: string;
  /**
   * JSON system context — see vyvern-license-server's
   * util/system-info.ts for the canonical shape. May be null when the
   * customer didn't opt in.
   */
  systemInfo: string | null;
  receivedAt: number;
  /** Timestamp of the latest message on the thread (drives inbox sort). */
  lastMessageAt: number;
  /** `data-fb-name` labels from the component picker (may be empty). */
  components: string[];
  status: FeedbackStatus;
  tags: string[];
  redactedAt: number | null;
  redactedBy: string | null;
  redactedFields: RedactableFeedbackField[];
  /** True if the row carries a screenshot — list-rows omit the actual bytes. */
  hasScreenshot: boolean;
}

/**
 * One row in the feedback_messages thread. User messages alternate with
 * staff replies. `body` is "[REDACTED]" if `redactedAt` is set.
 */
export interface FeedbackThreadMessage {
  id: string;
  sender: "user" | "staff";
  senderName: string | null;
  body: string;
  createdAt: number;
  transport: "in_app" | "email";
  deliveredAt: number | null;
  readByUserAt: number | null;
  readByStaffAt: number | null;
}

/**
 * Customer card surfaced on the feedback detail pane. Null when the
 * row was submitted anonymously or its license/customer has been
 * deleted. Tombstoned customers still appear (masked email + the
 * tombstone marker) so staff know the row maps to a real account.
 */
export interface FeedbackDetailCustomer {
  id: string;
  emailMasked: string;
  displayName: string | null;
  country: string | null;
  feedbackEnabled: boolean;
  tombstonedAt: number | null;
  tier: "free" | "pro" | "beta";
  betaProgram: boolean;
}

export interface FeedbackDetail extends Omit<FeedbackRow, "hasScreenshot"> {
  /** Base64-encoded PNG; only the detail route returns it. */
  screenshotBase64: string | null;
  /**
   * Full message thread, chronological. The first row mirrors `body`
   * for legacy callers; new rows go here exclusively.
   */
  messages: FeedbackThreadMessage[];
  /** Joined customer info; null for anonymous / orphaned rows. */
  customer: FeedbackDetailCustomer | null;
}

export interface ListFeedbackParams {
  cursor?: string | null | undefined;
  limit?: number | undefined;
  status?: FeedbackStatus | undefined;
  tag?: string | undefined;
}

export interface UpdateFeedbackInput {
  status?: FeedbackStatus;
  tags?: string[];
}

export const FeedbackApi = {
  list: (p: ListFeedbackParams = {}) =>
    apiPaged<FeedbackRow>(
      `/admin/feedback${qs({
        cursor: p.cursor ?? undefined,
        limit: p.limit ?? undefined,
        status: p.status ?? undefined,
        tag: p.tag ?? undefined,
      })}`,
    ),
  get: (id: string) => api<FeedbackDetail>("GET", `/admin/feedback/${encodeURIComponent(id)}`),
  update: (id: string, input: UpdateFeedbackInput) =>
    api<FeedbackDetail | null>("PATCH", `/admin/feedback/${encodeURIComponent(id)}`, input),
  reply: (id: string, body: string) =>
    api<FeedbackThreadMessage>("POST", `/admin/feedback/${encodeURIComponent(id)}/reply`, { body }),
  redact: (id: string, fields: RedactableFeedbackField[]) =>
    api<{ redacted: RedactableFeedbackField[] }>(
      "POST",
      `/admin/feedback/${encodeURIComponent(id)}/redact`,
      { fields },
    ),
} as const;

// ─── customers (Slice 43) ────────────────────────────────────────────────

export const LAWFUL_BASES = ["contract", "consent", "legitimate_interest"] as const;
export type LawfulBasis = (typeof LAWFUL_BASES)[number];

export const CONSENT_PURPOSES = ["telemetry", "marketing", "product_news"] as const;
export type ConsentPurpose = (typeof CONSENT_PURPOSES)[number];

export const CONSENT_SOURCES = ["in_app", "email", "admin"] as const;
export type ConsentSource = (typeof CONSENT_SOURCES)[number];

export interface CustomerRow {
  id: string;
  emailMasked: string;
  displayName: string | null;
  country: string | null;
  isEea: boolean;
  lawfulBasis: LawfulBasis;
  notes: string | null;
  /** When false, the customer can't open new feedback tickets / append. */
  feedbackEnabled: boolean;
  createdAt: number;
  updatedAt: number;
  tombstonedAt: number | null;
}

export interface ConsentRow {
  id: string;
  customerId: string;
  purpose: string;
  granted: boolean;
  source: ConsentSource;
  at: number;
  evidence: string | null;
}

export interface CustomerLinkedLicense {
  licenseId: string;
  tier: Tier;
  revoked: boolean;
  expiresAt: number | null;
  createdAt: number;
}

export interface CustomerDetail extends CustomerRow {
  consentLedger: ConsentRow[];
  currentConsent: Record<string, boolean>;
  linkedLicenses: CustomerLinkedLicense[];
  feedbackCount: number;
}

export interface ListCustomersParams {
  cursor?: string | null | undefined;
  limit?: number | undefined;
  q?: string | undefined;
  // Slice 55 — server-side filters fed by FilterBar.
  tag?: string | undefined;
  country?: string | undefined;
}

export interface CreateCustomerInput {
  email: string;
  displayName?: string;
  country?: string;
  lawfulBasis: LawfulBasis;
  notes?: string;
}

export interface UpdateCustomerInput {
  displayName?: string | null;
  country?: string | null;
  notes?: string | null;
  lawfulBasis?: LawfulBasis;
}

export interface ExportBundle {
  generatedAt: number;
  article: string;
  customer: unknown;
  consent: ConsentRow[];
  licenses: unknown[];
  feedback: unknown[];
}

// ─── settings + staff + audit-list (Slice 47) ────────────────────────────

export interface SettingsRollup {
  generatedAt: number;
  gdpr: {
    dataController: string;
    dpoEmail: string;
    primaryRegion: string;
    openDsrCount: number;
    activeCustomers: number;
    activeSubprocessors: number;
    ropaVersion: string;
  };
  signing: {
    describe: string;
    publicKeyHex: string | null;
    proxyHealthy: boolean;
  };
  retention: RetentionPolicy | null;
  licenseServer: {
    version: string;
    mode: "air-gapped" | "single-host";
  };
}

export interface ProxyTestResult {
  ok: boolean;
  publicKeyHex?: string;
  latencyMs?: number;
  describe: string;
  error?: string;
}

export interface StaffRow {
  id: string;
  username: string;
  role: StaffRole;
  createdAt: number;
  lastLoginAt: number | null;
  credentialCount: number;
  hasPassword: boolean;
}

// ─── invites + credentials (Slice 48 V2.1) ────────────────────────────────

export interface StaffInvite {
  id: string;
  username: string;
  email: string | null;
  role: StaffRole;
  invitedBy: string;
  createdAt: number;
  expiresAt: number;
  claimedAt: number | null;
  revokedAt: number | null;
  status: "open" | "claimed" | "revoked" | "expired";
}

export interface StaffInviteCreated {
  id: string;
  token: string;
  url: string;
  username: string;
  email: string | null;
  role: StaffRole;
  invitedBy: string;
  createdAt: number;
  expiresAt: number;
}

export interface StaffInvitePreview {
  username: string;
  email: string | null;
  role: StaffRole;
  invitedBy: string;
  expiresAt: number;
}

export interface OwnCredential {
  id: string;
  deviceName: string | null;
  transports: string[];
  createdAt: number;
  lastUsedAt: number | null;
}

export interface AuditRow {
  id: string;
  at: number;
  actor: string;
  action: string;
  targetType: string;
  targetId: string | null;
  meta: unknown;
  ipHash: string | null;
}

export interface ListAuditParams {
  cursor?: string | null | undefined;
  limit?: number | undefined;
  action?: string | undefined;
  actor?: string | undefined;
  /** Slice 51 — LIKE-search over meta JSON + targetId. */
  q?: string | undefined;
  /** Slice 51 — unix-ms range over `at`. */
  from?: number | undefined;
  to?: number | undefined;
}

export interface AuthPolicy {
  requirePasskey: boolean;
  updatedAt: number;
  updatedBy: string;
}

export const SettingsApi = {
  get: () => api<SettingsRollup>("GET", "/admin/settings"),
  testSigningProxy: () => api<ProxyTestResult>("POST", "/admin/settings/test-signing-proxy"),
  // Slice 49 (V2.2) — auth-policy toggle.
  getAuth: () => api<AuthPolicy>("GET", "/admin/settings/auth"),
  setAuth: (requirePasskey: boolean) =>
    api<AuthPolicy>("PUT", "/admin/settings/auth", { requirePasskey }),
  // Slice 52 (Phase 8) — signing-key registry.
  signingKeys: () => api<SigningKeyEntry[]>("GET", "/admin/signing-keys"),
  // Slice 52b — mutations.
  addSigningKey: (input: { id: string; publicKeyHex: string; proxySlot?: string }) =>
    api<SigningKeyEntry>("POST", "/admin/signing-keys", input),
  retireSigningKey: (id: string) =>
    api<{ id: string; retiredAt: number | null; alreadyRetired: boolean }>(
      "POST",
      `/admin/signing-keys/${encodeURIComponent(id)}/retire`,
    ),
} as const;

export interface SigningKeyEntry {
  id: string;
  publicKeyHex: string;
  proxySlot: string | null;
  addedAt: number;
  retiredAt: number | null;
}

export const StaffApi = {
  list: () => api<StaffRow[]>("GET", "/admin/staff"),
  updateRole: (id: string, role: StaffRole) =>
    api<{ id: string; role: StaffRole; unchanged?: boolean }>(
      "PATCH",
      `/admin/staff/${encodeURIComponent(id)}`,
      { role },
    ),
  remove: (id: string) =>
    api<{ id: string; removed: true }>("DELETE", `/admin/staff/${encodeURIComponent(id)}`),
} as const;

export const InvitesApi = {
  list: () => api<StaffInvite[]>("GET", "/admin/staff/invites"),
  create: (input: { username: string; email?: string; role: StaffRole }) =>
    api<StaffInviteCreated>("POST", "/admin/staff/invites", input),
  revoke: (id: string) =>
    api<{ id: string; revoked: true; alreadyRevoked?: boolean }>(
      "POST",
      `/admin/staff/invites/${encodeURIComponent(id)}/revoke`,
    ),
  preview: (token: string) =>
    api<StaffInvitePreview>("GET", `/admin/invites/${encodeURIComponent(token)}`),
  /** WebAuthn begin — returns PublicKeyCredentialCreationOptionsJSON. */
  begin: (token: string) =>
    api<unknown>("POST", `/admin/invites/${encodeURIComponent(token)}/begin`),
  /** WebAuthn finish — registration response payload from @simplewebauthn/browser. */
  finish: (token: string, response: unknown, deviceName?: string) =>
    api<{ username: string; role: StaffRole }>(
      "POST",
      `/admin/invites/${encodeURIComponent(token)}/finish`,
      deviceName ? { response, deviceName } : { response },
    ),
} as const;

export const PasskeyApi = {
  list: () => api<OwnCredential[]>("GET", "/admin/me/credentials"),
  beginAdd: () => api<unknown>("POST", "/admin/me/credentials/begin-add"),
  finishAdd: (response: unknown, deviceName?: string) =>
    api<{ id: string; deviceName: string | null }>(
      "POST",
      "/admin/me/credentials/finish-add",
      deviceName ? { response, deviceName } : { response },
    ),
  remove: (id: string) =>
    api<{ id: string; removed: true }>("DELETE", `/admin/me/credentials/${encodeURIComponent(id)}`),
} as const;

// ─── recovery (Slice 50, Phase 8) ────────────────────────────────────────

export interface RecoveryStats {
  remaining: number;
  generatedAt: number | null;
}

export interface RecoveryCodesIssued {
  codes: string[];
  generatedAt: number;
}

export interface RecoveryToken {
  recoveryToken: string;
  expiresAt: number;
}

export const RecoveryApi = {
  stats: () => api<RecoveryStats>("GET", "/admin/me/recovery-codes"),
  regenerate: () => api<RecoveryCodesIssued>("POST", "/admin/me/recovery-codes/regenerate"),
  begin: (username: string, code: string) =>
    api<RecoveryToken>("POST", "/admin/recovery/begin", { username, code }),
  finish: (token: string, newPassword: string) =>
    api<{ username: string; role: StaffRole }>("POST", "/admin/recovery/finish", {
      token,
      newPassword,
    }),
} as const;

export const AuditApi = {
  list: (p: ListAuditParams = {}) =>
    apiPaged<AuditRow>(
      `/admin/audit${qs({
        cursor: p.cursor ?? undefined,
        limit: p.limit ?? undefined,
        action: p.action ?? undefined,
        actor: p.actor ?? undefined,
        q: p.q ?? undefined,
        from: p.from ?? undefined,
        to: p.to ?? undefined,
      })}`,
    ),
} as const;

// ─── overview (Slice 46) ─────────────────────────────────────────────────

export interface OverviewData {
  generatedAt: number;
  weekday: string;
  kpi: {
    activeLicenses: number;
    newFeedback: number;
    openDsr: number;
    betaCohort: number;
    totalCustomers: number;
  };
  pendingDsr: { count: number; dangerCount: number };
  activations: {
    totalLast30: number;
    series: { date: string; count: number }[];
  };
  funnel: { key: string; label: string; count: number }[];
  recentFeedback: {
    id: string;
    licenseId: string | null;
    vyvernVersion: string;
    body: string;
    receivedAt: number;
    status: string;
  }[];
  recentActivity: {
    id: string;
    at: number;
    actor: string;
    action: string;
    targetType: string;
    targetId: string | null;
  }[];
}

export const OverviewApi = {
  get: () => api<OverviewData>("GET", "/admin/overview"),
} as const;

// ─── privacy: DSR + retention + subprocessors + consent rollup (Slice 45)

export const DSR_KINDS = ["access", "erase", "rectify", "portability", "object"] as const;
export type DsrKind = (typeof DSR_KINDS)[number];

export const DSR_STATUSES = ["open", "verifying", "fulfilled", "rejected"] as const;
export type DsrStatus = (typeof DSR_STATUSES)[number];

export interface DsrRow {
  id: string;
  customerId: string | null;
  emailMasked: string;
  kind: DsrKind;
  status: DsrStatus;
  receivedAt: number;
  deadlineAt: number;
  warnAt: number;
  closedAt: number | null;
  resolution: string | null;
  verified: boolean;
  verificationNote: string | null;
  auditId: string | null;
}

export interface RetentionPolicy {
  id: "current";
  feedbackDays: number;
  telemetryDays: number;
  dsrClosedDays: number;
  ipHashDays: number;
  auditYears: number;
  updatedAt: number;
  updatedBy: string;
}

export interface Subprocessor {
  id: string;
  name: string;
  purpose: string;
  location: string;
  dpaUrl: string | null;
  transferMechanism: string;
  addedAt: number;
  removedAt: number | null;
}

export interface ConsentRollup {
  totalCustomers: number;
  byPurpose: { purpose: string; optInCount: number; percentage: number }[];
}

export interface RetentionRunSummary {
  ranAt: number;
  feedbackDeleted: number;
  telemetryDeleted: number;
  dsrClosedDeleted: number;
  ipHashClearedAudit: number;
  ipHashClearedSessions: number;
  dsrPastWarn: { id: string; deadlineAt: number; daysLeft: number }[];
  policyUsed: Omit<RetentionPolicy, "id" | "updatedAt" | "updatedBy">;
}

export const DsrApi = {
  list: (status?: DsrStatus) =>
    api<DsrRow[]>("GET", `/admin/dsr${status ? `?status=${status}` : ""}`),
  create: (email: string, kind: DsrKind) =>
    api<DsrRow | null>("POST", "/admin/dsr", { email, kind }),
  verify: (id: string, method: string, note?: string) =>
    api<DsrRow | null>("POST", `/admin/dsr/${encodeURIComponent(id)}/verify`, {
      method,
      ...(note ? { note } : {}),
    }),
  fulfil: (id: string, resolution: string) =>
    api<{ dsr: DsrRow | null; exportBundle: unknown; erased: boolean }>(
      "POST",
      `/admin/dsr/${encodeURIComponent(id)}/fulfil`,
      { resolution },
    ),
  reject: (id: string, reason: string) =>
    api<DsrRow | null>("POST", `/admin/dsr/${encodeURIComponent(id)}/reject`, { reason }),
} as const;

export const RetentionApi = {
  get: () => api<RetentionPolicy>("GET", "/admin/retention"),
  update: (input: Partial<Omit<RetentionPolicy, "id" | "updatedAt" | "updatedBy">>) =>
    api<RetentionPolicy>("PUT", "/admin/retention", input),
  run: () => api<RetentionRunSummary>("POST", "/admin/retention/run"),
} as const;

export const SubprocessorsApi = {
  list: (includeRemoved = false) =>
    api<Subprocessor[]>(
      "GET",
      `/admin/subprocessors${includeRemoved ? "?includeRemoved=true" : ""}`,
    ),
  create: (input: {
    name: string;
    purpose: string;
    location: string;
    dpaUrl?: string;
    transferMechanism?: string;
  }) => api<Subprocessor>("POST", "/admin/subprocessors", input),
  remove: (id: string) =>
    api<{ id: string; alreadyRemoved: boolean }>(
      "DELETE",
      `/admin/subprocessors/${encodeURIComponent(id)}`,
    ),
} as const;

export const ConsentRollupApi = {
  get: () => api<ConsentRollup>("GET", "/admin/consent-rollup"),
} as const;

// ─── telemetry (Slice 44) ────────────────────────────────────────────────

export interface TelemetrySummary {
  window: { fromTs: number; toTs: number; days: number };
  reportingInstances: number;
  activeInstances: number;
  commandInvocations: number;
  errorTotal: number;
  errorRate: number;
  byVersion: { vyvernVersion: string; count: number }[];
  feature: { key: string; count: number }[];
  errors: { key: string; count: number }[];
  // Slice 54 — license-expiry KPIs for the LicensesScreen strip.
  expiringLicensesCount: number;
  expiredLicensesCount: number;
}

export interface TelemetryRawRow {
  id: string;
  licenseId: string | null;
  vyvernVersion: string;
  fromTs: number;
  toTs: number;
  receivedAt: number;
  metrics: {
    instanceCount: number;
    featureUsage: Record<string, number>;
    errorCounts: Record<string, number>;
  };
}

export const TelemetryApi = {
  summary: (windowDays = 30) =>
    api<TelemetrySummary>("GET", `/admin/telemetry/summary?windowDays=${windowDays}`),
  raw: (cursor: string | null = null, limit = 50) =>
    apiPaged<TelemetryRawRow>(`/admin/telemetry/raw${qs({ cursor: cursor ?? undefined, limit })}`),
} as const;

export const CustomersApi = {
  list: (p: ListCustomersParams = {}) =>
    apiPaged<CustomerRow>(
      `/admin/customers${qs({
        cursor: p.cursor ?? undefined,
        limit: p.limit ?? undefined,
        q: p.q ?? undefined,
        tag: p.tag ?? undefined,
        country: p.country ?? undefined,
      })}`,
    ),
  tagsSummary: () => api<{ tag: string; count: number }[]>("GET", "/admin/customers-tags-summary"),
  create: (input: CreateCustomerInput) =>
    api<CustomerRow | null>("POST", "/admin/customers", input),
  get: (id: string) => api<CustomerDetail>("GET", `/admin/customers/${encodeURIComponent(id)}`),
  update: (id: string, input: UpdateCustomerInput) =>
    api<CustomerRow | null>("PATCH", `/admin/customers/${encodeURIComponent(id)}`, input),
  revealEmail: (id: string, reason: string) =>
    api<{ email: string }>("POST", `/admin/customers/${encodeURIComponent(id)}/email/reveal`, {
      reason,
    }),
  exportData: (id: string) =>
    api<ExportBundle>("POST", `/admin/customers/${encodeURIComponent(id)}/export`),
  erase: (id: string) =>
    api<{ tombstoned: true; alreadyErased: boolean }>(
      "POST",
      `/admin/customers/${encodeURIComponent(id)}/erase`,
      { confirm: "yes" },
    ),
  recordConsent: (
    id: string,
    purpose: ConsentPurpose,
    granted: boolean,
    source: ConsentSource,
    evidence?: string,
  ) =>
    api<{ id: string; at: number }>("POST", `/admin/customers/${encodeURIComponent(id)}/consent`, {
      purpose,
      granted,
      source,
      ...(evidence ? { evidence } : {}),
    }),
  /** Per-customer feedback kill-switch. */
  setFeedbackEnabled: (id: string, enabled: boolean) =>
    api<{ feedbackEnabled: boolean }>(
      "POST",
      `/admin/customers/${encodeURIComponent(id)}/feedback-toggle`,
      { enabled },
    ),
  // ── Slice 53 — customer drill-down (CustomerDetailScreen) ──
  feedbackThreads: (id: string) =>
    api<CustomerFeedbackThread[]>("GET", `/admin/customers/${encodeURIComponent(id)}/feedback`),
  instances: (id: string) =>
    api<CustomerInstanceRow[]>("GET", `/admin/customers/${encodeURIComponent(id)}/instances`),
  auditTimeline: (id: string, limit = 200) =>
    api<CustomerAuditRow[]>(
      "GET",
      `/admin/customers/${encodeURIComponent(id)}/audit?limit=${limit}`,
    ),
  listTags: (id: string) =>
    api<CustomerTagRow[]>("GET", `/admin/customers/${encodeURIComponent(id)}/tags`),
  addTag: (id: string, tag: string) =>
    api<CustomerTagRow & { alreadyTagged?: boolean }>(
      "POST",
      `/admin/customers/${encodeURIComponent(id)}/tags`,
      { tag },
    ),
  removeTag: (id: string, tag: string) =>
    api<{ removed: true }>(
      "DELETE",
      `/admin/customers/${encodeURIComponent(id)}/tags/${encodeURIComponent(tag)}`,
    ),
  suspend: (id: string, reason?: string) =>
    api<{ suspended: number }>(
      "POST",
      `/admin/customers/${encodeURIComponent(id)}/suspend`,
      reason ? { reason } : {},
    ),
  unsuspend: (id: string) =>
    api<{ unsuspended: number }>("POST", `/admin/customers/${encodeURIComponent(id)}/unsuspend`),
  resendLicenseEmail: (id: string) =>
    api<{ sent: number }>(
      "POST",
      `/admin/customers/${encodeURIComponent(id)}/resend-license-email`,
    ),
} as const;

// ─── Slice 53 — customer drill-down shapes ────────────────────────────────

export interface CustomerFeedbackThread {
  id: string;
  licenseId: string | null;
  vyvernVersion: string;
  status: "new" | "triaged" | "in_progress" | "closed";
  tags: unknown[];
  receivedAt: number;
  lastMessageAt: number;
  redactedAt: number | null;
  bodyPreview: string;
  unreadFromUser: number;
}

export interface CustomerInstanceRow {
  licenseId: string;
  tier: Tier;
  revoked: boolean;
  lastSeenTs: number | null;
  instanceCount: number;
  version: string | null;
  topFeature: string | null;
  errorRate: number | null;
}

export interface CustomerAuditRow {
  id: string;
  at: number;
  actor: string;
  action: string;
  targetType: string;
  targetId: string | null;
  meta: Record<string, unknown>;
  ipHash: string | null;
}

export interface CustomerTagRow {
  id: string;
  customerId: string;
  tag: string;
  createdAt: number;
  createdBy: string;
}
